import type {
  StaticSupportPlaybook,
  StaticSupportPlaybookLink,
  StaticSupportPlaybookStep,
} from './supportPlaybooks';
import { getUsSupportRoute } from './supportUsRouting';

const PLAYBOOK_REVIEWED_AT = '2026-04-21T19:00:00.000Z';

const LINKS = {
  benefitsFinder: getUsSupportRoute('benefits_gov_finder'),
  snapDirectory: getUsSupportRoute('snap_state_directory'),
  medicaidStates: getUsSupportRoute('medicaid_state_overviews'),
  childSupportContacts: getUsSupportRoute('child_support_contacts'),
  childcareStateResources: getUsSupportRoute('childcare_state_resources'),
  childcareFinancialAssistance: getUsSupportRoute('childcare_financial_assistance'),
  headStart: getUsSupportRoute('head_start_locator'),
  phaContacts: getUsSupportRoute('hud_pha_contacts'),
  emergencyRent: getUsSupportRoute('emergency_rent_help'),
  localHelp: getUsSupportRoute('help_211'),
  tenantRights: getUsSupportRoute('tenant_rights'),
  legalHelp: getUsSupportRoute('lsc_legal_help'),
  lawHelp: getUsSupportRoute('lawhelp_org'),
  parentCenter: getUsSupportRoute('parent_center_hub'),
  districtSearch: getUsSupportRoute('nces_district_search'),
  mckinneyVento: getUsSupportRoute('nche_mckinney_vento'),
  localJobs: getUsSupportRoute('careeronestop_local_help'),
  stateLabor: getUsSupportRoute('state_labor_offices'),
  selfEmployment: getUsSupportRoute('irs_self_employed_tax_center'),
  estimatedTaxes: getUsSupportRoute('irs_estimated_taxes'),
  mentalHealth: getUsSupportRoute('find_treatment'),
} as const;

type StepInput = Omit<StaticSupportPlaybookStep, 'step'>;

const withLinks = (...links: Array<StaticSupportPlaybookLink | null | undefined>) =>
  links.filter((link): link is StaticSupportPlaybookLink => Boolean(link));

const buildPlaybook = (definition: {
  slug: string;
  title: string;
  description: string;
  icon: string;
  life_event_tags: string[];
  steps: StepInput[];
}): StaticSupportPlaybook => ({
  id: `static-us-${definition.slug}`,
  slug: definition.slug,
  section: 'support',
  title: definition.title,
  description: definition.description,
  icon: definition.icon,
  difficulty: 'moderate',
  estimated_time: `${definition.steps.length} steps`,
  life_event_tags: definition.life_event_tags,
  steps: definition.steps.map((step, index) => ({
    ...step,
    step: index + 1,
  })),
  is_active: true,
  sort_order: 0,
  last_reviewed_at: PLAYBOOK_REVIEWED_AT,
  review_frequency_months: 6,
  created_at: PLAYBOOK_REVIEWED_AT,
  updated_at: PLAYBOOK_REVIEWED_AT,
});

export const getUnitedStatesStaticSupportPlaybook = (
  slug: string,
): StaticSupportPlaybook | null => {
  switch (slug) {
    case 'moving-house':
      return buildPlaybook({
        slug,
        title: 'Moving House as a Single Parent in the U.S.',
        description:
          'The practical first steps from lease checks to school updates, with U.S. routing where housing, benefits, and district rules can shift quickly.',
        icon: '📦',
        life_event_tags: ['housing', 'moving', 'single parent'],
        steps: [
          {
            title: 'Check the lease and notice rules first',
            description:
              'Check the lease, notice period, and any move-out rules before you promise a date. Keep everything in writing.',
            actions: ['Keep the lease, notices, and landlord messages in one live folder.'],
            links: withLinks(LINKS.tenantRights),
          },
          {
            title: 'Work out the school district before you move',
            description:
              'District boundaries, enrollment rules, and school transport can change the whole picture.',
            actions: ['Check the district before you lock the new address in.'],
            links: withLinks(LINKS.districtSearch),
          },
          {
            title: 'Update benefits and cases quickly',
            description:
              'Update benefits and case addresses fast: SNAP, Medicaid/CHIP, TANF, child support, unemployment, and any housing program.',
            actions: ['Treat address updates as same-week admin, not later admin.'],
            links: withLinks(LINKS.snapDirectory, LINKS.medicaidStates, LINKS.childSupportContacts),
          },
          {
            title: 'Pull local crisis help forward if the move is unstable',
            description:
              'If the move is because housing is failing, call 211 and check emergency rent or shelter options before the last day becomes the crisis day.',
            actions: ['Move emergency housing and rent help higher up the list when time is short.'],
            links: withLinks(LINKS.localHelp, LINKS.emergencyRent),
          },
          {
            title: 'Protect yourself on deposit and repairs',
            description:
              'Photograph the property, keep repair emails, and save proof of how you left it in case deposit arguments start later.',
            actions: ['Save photos, final readings, and any written condition notes.'],
          },
          {
            title: 'Move utilities deliberately',
            description:
              'Give final readings, set up the new address, and ask about crisis or low-income utility help if money is tight.',
            actions: ['Do not leave utility transfers until moving day if you can help it.'],
            links: withLinks(LINKS.localHelp),
          },
          {
            title: 'Update school, doctors, and emergency contacts',
            description:
              'Update the school, doctors, and emergency contacts as soon as the new address is real. If your child has an IEP or 504, flag the move early.',
            actions: ['Tell the school support team early if your child has extra support needs.'],
          },
          {
            title: 'Give the first week a soft landing',
            description:
              'Beds, medication, school bags, chargers, and one calm corner matter more than perfect unpacking.',
            actions: ['Aim for safe and steady first, not beautifully unpacked.'],
          },
        ],
      });
    case 'going-self-employed':
      return buildPlaybook({
        slug,
        title: 'Setting Up Self-Employed from Home in the U.S.',
        description:
          'A calm first pass on self-employment admin, taxes, benefits, and family reality before the work gets bigger than your systems.',
        icon: '💻',
        life_event_tags: ['work', 'self-employment', 'tax'],
        steps: [
          {
            title: 'Choose the simplest structure first',
            description:
              'Choose the simplest starting structure unless you have a clear reason to do something more complex. Many people begin as sole proprietors.',
            actions: ['Do not overcomplicate day one if the business is still small and early.'],
            links: withLinks(LINKS.selfEmployment),
          },
          {
            title: 'Separate the money early',
            description:
              'Open a separate account for business money, even if the law does not force you to. It saves time, stress, and tax confusion.',
            actions: ['Create one place for income and expenses before work gets busy.'],
          },
          {
            title: 'Learn estimated taxes now',
            description:
              'Learn estimated taxes early. Set money aside from the beginning instead of hoping the first year will somehow sort itself out.',
            actions: ['Treat tax money as already spoken for.'],
            links: withLinks(LINKS.estimatedTaxes),
          },
          {
            title: 'Keep records from day one',
            description:
              'Keep records from day one: income, expenses, receipts, invoices, mileage, and notes about mixed personal/business costs.',
            actions: ['Pick one boring system and keep using it.'],
            links: withLinks(LINKS.selfEmployment),
          },
          {
            title: 'Check health cover before income gets unstable',
            description:
              'Check health insurance before you commit to unstable income. Marketplace timing matters if job-based cover is ending.',
            actions: ['Do not let health coverage be an afterthought.'],
            links: withLinks(LINKS.medicaidStates),
          },
          {
            title: 'Work out the child care reality',
            description:
              'Work out child care and school-hour reality before you take on more than you can actually deliver.',
            actions: ['Protect school pickups, child care, and backup plans before promising capacity.'],
            links: withLinks(LINKS.childcareFinancialAssistance),
          },
          {
            title: 'Re-check benefits when income changes shape',
            description:
              'Re-check benefits if your income drops or changes shape. State systems do not always understand irregular self-employment income unless you report it clearly.',
            actions: ['Report clearly and keep copies when income is uneven.'],
            links: withLinks(LINKS.benefitsFinder, LINKS.snapDirectory),
          },
          {
            title: 'Build one simple admin rhythm',
            description:
              'Make one simple admin rhythm: one day for money, one place for receipts, one way to send invoices. Boring systems protect you later.',
            actions: ['A repeatable system beats heroic catch-up.'],
          },
        ],
      });
    case 'separation-divorce':
      return buildPlaybook({
        slug,
        title: 'Separation, Parenting, and Child Support - First Steps',
        description:
          'A practical U.S. first-pass when separation has blown everything open and you need to hold safety, paperwork, and the basics together.',
        icon: '⚖️',
        life_event_tags: ['separation', 'child support', 'co-parenting'],
        steps: [
          {
            title: 'Treat safety as the first decision',
            description:
              'If there is abuse, stalking, threats, or coercion, use a specialist domestic violence route, not just a general family-law article.',
            actions: ['Move specialist safety support above general family-law reading.'],
            links: withLinks(getUsSupportRoute('domestic_violence_hotline')),
          },
          {
            title: 'Write down the real care pattern',
            description:
              'Write down the care pattern as it is actually happening now: nights, pickups, missed contact, and who is paying for what.',
            actions: ['Use facts, dates, and routines, not emotional summaries.'],
          },
          {
            title: 'Do not assume the legal pieces solve themselves',
            description:
              'Do not assume custody, parenting time, or child support will sort themselves out automatically. They are often separate processes.',
            actions: ['Name each process separately so nothing important gets missed.'],
            links: withLinks(LINKS.lawHelp, LINKS.childSupportContacts),
          },
          {
            title: 'Find the right child support agency early',
            description:
              'Find your state or tribal child support agency early if money support is already unstable or absent.',
            actions: ['Use the official directory before guessing which office owns the case.'],
            links: withLinks(LINKS.childSupportContacts),
          },
          {
            title: 'Get legal help before you sign things you do not understand',
            description:
              'Use legal aid, court self-help, or specialist family-law support before you sign anything you do not understand.',
            actions: ['Slow the paperwork down if you do not understand it yet.'],
            links: withLinks(LINKS.legalHelp, LINKS.lawHelp),
          },
          {
            title: 'Update school and medical contacts carefully',
            description:
              'Update school and medical contacts carefully if there are safety concerns or new restrictions on contact.',
            actions: ['Only share what the school or clinic needs to keep routines and safety clear.'],
          },
          {
            title: 'Protect documents and access',
            description:
              'Protect your documents, passwords, immigration papers if relevant, and your child key records.',
            actions: ['Treat records, passwords, and identity documents as urgent admin.'],
          },
          {
            title: 'Build a 30-day survival plan',
            description:
              'Build a 30-day survival plan for money, housing, child care, and transport. The legal process is easier when the basics are held together.',
            actions: ['Hold the next month together first.'],
            links: withLinks(LINKS.benefitsFinder, LINKS.localHelp),
          },
        ],
      });
    case 'getting-ehcp':
      return buildPlaybook({
        slug,
        title: 'Getting Help at School for Your Child',
        description:
          'A U.S. school-support path that keeps concerns, records, and the right local routes visible from the start.',
        icon: '📚',
        life_event_tags: ['education', 'school support', 'additional needs'],
        steps: [
          {
            title: 'Write down what you are seeing',
            description:
              'Write down what you are seeing at home and what school is reporting. Specific examples are more useful than broad labels.',
            actions: ['Keep concrete examples, dates, and patterns together.'],
          },
          {
            title: 'Start with the school team',
            description:
              'Start with the teacher, counselor, SEN or special education contact, or school team that handles support concerns.',
            actions: ['Start with the person who already sees your child most often.'],
          },
          {
            title: 'Ask in writing if concerns continue',
            description:
              'If concerns are continuing, ask for an evaluation or screening in writing so there is a record and a date.',
            actions: ['Written requests create a timeline you can work from.'],
            links: withLinks(getUsSupportRoute('idea_initial_evaluations')),
          },
          {
            title: 'Keep copies of everything',
            description:
              'Keep copies of everything: emails, meeting notes, report cards, discipline records, and outside evaluations.',
            actions: ['Use one folder for every school support record.'],
          },
          {
            title: 'Work out which route fits',
            description:
              'Ask whether the issue sounds like classroom support, a 504 plan, a special education evaluation, or more than one of those.',
            actions: ['Do not let the school turn every concern into one vague conversation.'],
            links: withLinks(getUsSupportRoute('section_504'), getUsSupportRoute('idea_parents_families')),
          },
          {
            title: 'Find your Parent Center early',
            description:
              'Find your state Parent Center early. They are often the quickest route to clear, practical help.',
            actions: ['Treat Parent Centers as a first support route, not a last resort.'],
            links: withLinks(LINKS.parentCenter),
          },
          {
            title: 'Learn the dispute options before things harden',
            description:
              'If you disagree with the school, learn the dispute options before the relationship gets so strained that nobody is listening.',
            actions: ['Understand the next step before you urgently need it.'],
            links: withLinks(LINKS.lawHelp),
          },
          {
            title: 'Set your own review date',
            description:
              'Set a review date for yourself. Support at school needs follow-up; it rarely stays on track by accident.',
            actions: ['Follow-up is part of the support plan, not an extra.'],
          },
        ],
      });
    case 'going-back-to-work':
      return buildPlaybook({
        slug,
        title: 'Going Back to Work After a Gap',
        description:
          'A calmer return-to-work plan that tests the real numbers before you commit to a role that only works on paper.',
        icon: '💼',
        life_event_tags: ['work', 'return to work', 'child care'],
        steps: [
          {
            title: 'Do the real numbers first',
            description:
              'Work out the real numbers first: take-home pay, child care, transport, school breaks, health coverage, and what happens if a child gets sick.',
            actions: ['Let the real monthly picture decide what is sustainable.'],
          },
          {
            title: 'Update the resume calmly',
            description:
              'Update the resume with plain, calm wording. A family-related gap does not need a dramatic explanation.',
            actions: ['Neutral wording is usually enough.'],
          },
          {
            title: 'Use an American Job Center or local workforce service',
            description:
              'Use an American Job Center or local workforce service for resume help, training, and realistic local roles.',
            actions: ['Local support is often more useful than generic job advice.'],
            links: withLinks(LINKS.localJobs),
          },
          {
            title: 'Check what earning more changes',
            description:
              'Check whether earning more changes benefits before you accept a job that only works on paper.',
            actions: ['Re-check benefits before you say yes.'],
            links: withLinks(LINKS.benefitsFinder),
          },
          {
            title: 'Learn the state leave rules now',
            description:
              'If your state has paid leave or paid sick leave, learn the rule now, not on the first emergency day.',
            actions: ['Know the leave floor before the first crisis week.'],
            links: withLinks(LINKS.stateLabor),
          },
          {
            title: 'Plan the first month on purpose',
            description:
              'Plan the first month: school drop-off, backup pickup, lunches, uniforms, commute, and one backup contact.',
            actions: ['A basic first-month plan reduces the wobble.'],
          },
          {
            title: 'Leave margin if you are coming back after burnout',
            description:
              'If you are coming back after burnout, grief, or mental health strain, build in some margin rather than using every available hour immediately.',
            actions: ['Margin is part of returning well, not wasted capacity.'],
            links: withLinks(LINKS.mentalHealth),
          },
          {
            title: 'Choose the sustainable return',
            description:
              'Choose the version of returning that is sustainable, not just the version that sounds impressive to someone else.',
            actions: ['Steady beats performative.'],
          },
        ],
      });
    case 'benefits-check':
      return buildPlaybook({
        slug,
        title: 'When Rent, Bills, and Benefits Stop Making Sense',
        description:
          'A crisis-order pass through the fragmented U.S. support system when money has started to break faster than the paperwork can catch up.',
        icon: '💰',
        life_event_tags: ['money', 'benefits', 'housing crisis'],
        steps: [
          {
            title: 'Write down the urgent deadlines first',
            description:
              'Write down the urgent deadlines first: rent due date, shutoff warning, court date, interview, recertification, and food situation.',
            actions: ['The next deadline matters more than solving everything at once.'],
          },
          {
            title: 'Call 211 early',
            description:
              'Call 211 early. It is often the fastest route to local housing, food, utility, and emergency support.',
            actions: ['Use the live local directory before the crisis hardens.'],
            links: withLinks(LINKS.localHelp),
          },
          {
            title: 'Check the big four straight away',
            description:
              'Check the big four straight away if money has fallen apart: SNAP, Medicaid/CHIP, TANF if relevant, and LIHEAP.',
            actions: ['Do the biggest systems first while you still have energy.'],
            links: withLinks(LINKS.snapDirectory, LINKS.medicaidStates, getUsSupportRoute('tanf_start'), getUsSupportRoute('liheap_start')),
          },
          {
            title: 'Talk to the landlord or utility early if it is safe',
            description:
              'Talk to the landlord or utility before the missed payment turns into a legal or shutoff problem if you can do so safely.',
            actions: ['Earlier conversations usually leave more options on the table.'],
          },
          {
            title: 'Use immediate food help if you need it',
            description:
              'Use immediate food help when needed. Panic makes everything worse; getting through this week matters.',
            actions: ['Solve this week food first if that is the real pressure point.'],
            links: withLinks(getUsSupportRoute('emergency_food_help')),
          },
          {
            title: 'Avoid panic borrowing where possible',
            description:
              'Avoid panic borrowing where possible. High-cost credit can make a short crisis much longer.',
            actions: ['Protect future breathing room where you can.'],
          },
          {
            title: 'Move legal help up if notices already exist',
            description:
              'If notices or court papers are already involved, move legal aid higher up the list. Do not wait until the hearing day.',
            actions: ['Notices and hearings change the order of work.'],
            links: withLinks(LINKS.legalHelp),
          },
          {
            title: 'Take one next step at a time',
            description:
              'Pick one next step at a time. The system is fragmented; your job is just to keep the next door opening.',
            actions: ['You do not need the whole maze solved today.'],
          },
        ],
      });
    case 'renting-goes-wrong':
      return buildPlaybook({
        slug,
        title: 'When Renting Goes Wrong or an Eviction Threat Starts',
        description:
          'A first-response housing pack for records, legal help, rent support, and backup planning before the situation closes in.',
        icon: '🏠',
        life_event_tags: ['housing', 'eviction', 'renting'],
        steps: [
          {
            title: 'Keep every housing record together',
            description:
              'Keep every notice, text, email, rent ledger, and photo. Small records become important quickly in housing cases.',
            actions: ['Start one housing folder now, not later.'],
          },
          {
            title: 'Do not ignore court papers',
            description:
              'Do not ignore court papers even if the notice looks wrong. Missing the date can cost you options.',
            actions: ['Show up, read everything, and keep the dates visible.'],
          },
          {
            title: 'Use your state tenant-rights route and legal aid',
            description:
              'Use your state tenant-rights route and legal aid, not random social media advice from another state.',
            actions: ['Housing law changes too much by place for borrowed advice.'],
            links: withLinks(LINKS.tenantRights, LINKS.legalHelp),
          },
          {
            title: 'Be careful about partial payments',
            description:
              'Pay what you can if that is the right legal choice in your state, but keep clear records and get advice on the risk.',
            actions: ['Do not assume a payment helps without checking the state rule.'],
          },
          {
            title: 'Check rent help and local support early',
            description:
              'Check rent help, 211, church/community programs, and school-based family support routes early rather than after lockout risk.',
            actions: ['Pull local support forward before the deadline.'],
            links: withLinks(LINKS.localHelp, LINKS.emergencyRent),
          },
          {
            title: 'Prepare one clean document folder',
            description:
              'Prepare your documents in one folder: lease, notices, payment history, repairs, medical or school hardship evidence.',
            actions: ['Make it easy to answer questions quickly.'],
          },
          {
            title: 'Show up to every hearing or appointment',
            description:
              'Show up to every hearing, meeting, or housing authority appointment even if you feel underprepared.',
            actions: ['Presence keeps options alive.'],
          },
          {
            title: 'Build a backup housing plan as well',
            description:
              'Make a backup plan for emergency housing while you are still working the main plan. That is not giving up; it is protecting your children.',
            actions: ['Backup planning is protection, not defeat.'],
            links: withLinks(LINKS.phaContacts, LINKS.localHelp),
          },
        ],
      });
    case 'co-parenting-hard':
      return buildPlaybook({
        slug,
        title: 'Co-Parenting When It Is Hard',
        description:
          'A calm structure for written communication, routines, records, and safety when the co-parenting relationship is high-friction.',
        icon: '🤝',
        life_event_tags: ['co-parenting', 'family law', 'safety'],
        steps: [
          {
            title: 'Keep communication written and brief',
            description:
              'Keep communication written and brief where possible. Clear is kinder than over-explaining.',
            actions: ['Short, factual, and written usually travels better than long explanations.'],
          },
          {
            title: 'Work from the child routine first',
            description:
              'Work from the child routine first: school, sleep, medication, transport, and emotional safety.',
            actions: ['Let the child routine lead the planning.'],
          },
          {
            title: 'Record pattern changes without escalating every time',
            description:
              'Record missed handovers, changed plans, and missed support payments without turning every issue into a fight.',
            actions: ['Keep the record even when you do not act on it that day.'],
          },
          {
            title: 'Use one calendar and one notes trail',
            description:
              'Do not rely on memory when tension is high. Use one calendar, one notes trail, and one place for key information.',
            actions: ['One shared source of truth beats scattered screenshots.'],
          },
          {
            title: 'Get specialist advice for unsafe dynamics',
            description:
              'If the other parent is unsafe, manipulative, or unpredictable, get specialist advice instead of trying to out-negotiate them alone.',
            actions: ['Unsafe dynamics need specialist support, not better wording.'],
            links: withLinks(getUsSupportRoute('womenslaw_home'), LINKS.legalHelp),
          },
          {
            title: 'Tell schools and child care only what they need',
            description:
              'Tell schools and child care providers only what they need to know to keep routines and safety intact.',
            actions: ['Give them enough to keep the child safe, not the whole conflict history.'],
          },
          {
            title: 'Plan ahead for travel and moves',
            description:
              'Plan ahead for passports, travel letters, holidays, and interstate moves. These become harder when left late.',
            actions: ['Treat travel admin as early admin.'],
            links: withLinks(getUsSupportRoute('travel_state_minors')),
          },
          {
            title: 'Protect your peace where you can',
            description:
              'Protect your peace where you can. Not every message needs an instant answer, and not every provocation deserves your energy.',
            actions: ['Space can be part of the co-parenting plan too.'],
          },
        ],
      });
    case 'building-support-network':
      return buildPlaybook({
        slug,
        title: 'Building Your Support Network in a New Place',
        description:
          'A low-pressure way to turn a new place into a usable support map instead of waiting for instant belonging.',
        icon: '💗',
        life_event_tags: ['wellbeing', 'community', 'moving'],
        steps: [
          {
            title: 'Start with systems, not charisma',
            description:
              'Start with systems, not charisma: 211, your child school, community health centers, Parent Centers, and local library listings.',
            actions: ['Nearby systems are often easier to build from than social hope alone.'],
            links: withLinks(LINKS.localHelp, getUsSupportRoute('hrsa_health_centers'), LINKS.parentCenter),
          },
          {
            title: 'Ask one practical question everywhere',
            description:
              'Ask one practical question in each place: where do parents like me usually find help around here?',
            actions: ['One useful question often opens more doors than a long explanation.'],
          },
          {
            title: 'Use everyday parent places as connection points',
            description:
              'Use school, Head Start, child care, and activity pickups as quiet connection points rather than waiting for a perfect friend-making moment.',
            actions: ['Let routines do some of the work.'],
          },
          {
            title: 'Find the specialist network too when needed',
            description:
              'If disability, mental health, or caregiving is part of daily life, find the specialist network too, not just the general parent crowd.',
            actions: ['General support and specialist support do not have to come from the same place.'],
            links: withLinks(LINKS.parentCenter, LINKS.mentalHealth),
          },
          {
            title: 'Keep a short crisis contact list',
            description:
              'Keep a short crisis contact list: one person for school pickup, one for emotional support, one for practical help if possible.',
            actions: ['A short usable list beats a long imaginary network.'],
          },
          {
            title: 'Take the local option seriously',
            description:
              'Take the local option seriously even if it looks small. Nearby and reliable beats ideal and far away.',
            actions: ['Reliable and close often wins.'],
          },
          {
            title: 'Let support be layered',
            description:
              'Let support be layered: professional, practical, social, and emotional do not have to come from the same person.',
            actions: ['Stop asking one person or place to be everything.'],
          },
          {
            title: 'Treat support-building as admin too',
            description:
              'Building a network is admin as much as emotion. Treat it as part of settling in, not as a sign you are failing.',
            actions: ['Support admin is still real admin.'],
          },
        ],
      });
    case 'christmas-one-income':
      return buildPlaybook({
        slug,
        title: 'Holidays, Summer Break, and School Costs on One Income',
        description:
          'A steadier U.S. school-break plan for child care, food, free community options, and the budget reality around holidays and long breaks.',
        icon: '🎄',
        life_event_tags: ['school breaks', 'money', 'child care'],
        steps: [
          {
            title: 'Get the school calendar early',
            description:
              'Get the school calendar early and mark the long breaks before they become a surprise.',
            actions: ['Put the long breaks where you can see them early.'],
          },
          {
            title: 'Check summer and school-break coverage early',
            description:
              'Check meal programs, summer learning, camps, after-school routes, and district or city recreation options as soon as they open.',
            actions: ['These places fill up before the break actually starts.'],
            links: withLinks(LINKS.childcareStateResources, LINKS.localHelp),
          },
          {
            title: 'Re-check subsidy rules before school ends',
            description:
              'Re-check child care subsidy rules and provider bookings before school ends. Summer care gets tight fast.',
            actions: ['School-end timing matters more than perfect planning.'],
            links: withLinks(LINKS.childcareFinancialAssistance),
          },
          {
            title: 'Spread predictable costs where you can',
            description:
              'Spread predictable costs where you can: transport, food, activities, uniforms, and start-of-year fees.',
            actions: ['Visible costs are easier to plan for than surprise costs.'],
          },
          {
            title: 'Use free community anchors on purpose',
            description:
              'Use free community anchors on purpose: libraries, parks, splash pads, museums with free days, and local family events.',
            actions: ['Free and steady often works better than expensive and exhausting.'],
          },
          {
            title: 'Call 211 early if money is breaking',
            description:
              'If money is genuinely breaking, call 211 early for local support instead of carrying the whole plan alone.',
            actions: ['Local help is part of the plan, not evidence of failure.'],
            links: withLinks(LINKS.localHelp),
          },
          {
            title: 'Set expectations that fit your budget',
            description:
              'Set expectations that fit your budget. Children usually need steadiness, snacks, and some good memories more than an expensive plan.',
            actions: ['Aim for a doable season, not a performative one.'],
          },
          {
            title: 'Build rest in for yourself too',
            description:
              'Build rest into the plan for you as well. A school break that burns you out completely is not a good result.',
            actions: ['Protect your own energy as part of the family plan.'],
          },
        ],
      });
    default:
      return null;
  }
};
